import {
  createContext,
  ReactNode,
  RefObject,
  TransitionEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { blockKeyScroll, isIOS, preventScroll } from './utils';

import * as styles from './ComponentProductTiles.module.css';

import type { AnimationState, ContentfulComponentProductTiles } from './types';

const HERO_TRANSITION_DURATION = 600;
const HERO_TRANSITION_DURATION_TIMEOUT = HERO_TRANSITION_DURATION + 200;

type HeroContextType = {
  animationState: AnimationState;
  registerHeroCard: () => {
    ref?: RefObject<HTMLDivElement>;
    onTransitionEnd?: TransitionEventHandler;
  };
};

export const HeroContext = createContext<HeroContextType | undefined>(undefined);

type HeroProviderProps = {
  content: ContentfulComponentProductTiles;
  children?: ReactNode;
};

export const HeroProvider: React.FC<HeroProviderProps> = ({ content, children }) => {
  const hasVideo = !!content.video && content.video.length > 0;

  if (!hasVideo)
    return (
      <HeroContext.Provider
        value={{
          animationState: 'off',
          registerHeroCard: () => ({}),
        }}
      >
        {children}
      </HeroContext.Provider>
    );

  const cardRef = useRef<HTMLDivElement>(null);
  const animationTriggerRef = useRef<(_: Event) => void>(() => {});
  const [animationState, setAnimationState] = useState<AnimationState>(hasVideo ? 'initial' : 'off');

  const onAnimationStateInitial = () => {
    animationTriggerRef.current = (event: Pick<Event, 'type' | 'target' | 'preventDefault'>) => {
      if (event.type === 'wheel' && !!document.querySelector('.openMobileMenu') && window.innerWidth < 1152) return;

      // To prevent triggering the animation immediately after page reload. Some browser fire a scroll event.
      if (event.type === 'scroll' && window.scrollY === 0) return;

      event.preventDefault();
      window.scrollTo(0, 0);
      document.documentElement!.style.overflow = 'hidden';
      window.addEventListener('keydown', blockKeyScroll, { passive: false });
      window.addEventListener('scroll', preventScroll, { passive: false });
      setAnimationState('animating');
    };

    setTimeout(() => {
      cardRef.current?.addEventListener('click', animationTriggerRef.current, { once: false });
      cardRef.current?.addEventListener('touchstart', animationTriggerRef.current, { once: false, passive: false });
      window.addEventListener('wheel', animationTriggerRef.current, { once: false, passive: false });
      if (!isIOS()) window.addEventListener('scroll', animationTriggerRef.current, { once: false });
    }, 0);
  };

  const onAnimationStateAnimating = () => {
    if (!cardRef.current) return;

    const { top, right, left, bottom } = cardRef.current.getBoundingClientRect();

    const cardInner = cardRef.current.firstElementChild! as HTMLElement;
    const cardDummy = cardRef.current.lastElementChild! as HTMLElement;

    cardInner.style.inset = `${-top}px ${(document.documentElement.offsetWidth - right) * -1}px ${
      (window.innerHeight - bottom) * -1
    }px ${-left}px`;
    cardDummy.style.top = `${-top}px`;
    cardDummy.style.bottom = `${(window.innerHeight - bottom) * -1}px`;

    cardRef.current.offsetWidth;
    cardRef.current.classList.add(styles.animationTriggered);

    cardInner.style.inset = '0px';
    cardDummy.style.top = '0px';
    cardDummy.style.bottom = '0px';

    setTimeout(() => {
      if (animationState !== 'finished') onAnimationStateFinished();
    }, HERO_TRANSITION_DURATION_TIMEOUT);
  };

  const onAnimationStateFinished = () => {
    cardRef.current?.removeEventListener('click', animationTriggerRef.current);
    cardRef.current?.removeEventListener('touchstart', animationTriggerRef.current);
    window.removeEventListener('wheel', animationTriggerRef.current);
    window.removeEventListener('scroll', animationTriggerRef.current);
    window.removeEventListener('scroll', preventScroll);
    window.removeEventListener('keydown', blockKeyScroll);
    document.documentElement.style.overflow = '';
  };

  const onTransitionEnd: TransitionEventHandler<HTMLDivElement> = ({ propertyName }) => {
    if (propertyName === 'top' && animationState !== 'finished') setAnimationState('finished');
  };

  const registerHeroCard = () => ({ ref: cardRef, onTransitionEnd });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.setProperty('--heroTransitionDuration', `${HERO_TRANSITION_DURATION}ms`);

    return () => {
      onAnimationStateFinished();
    };
  }, []);

  useEffect(() => {
    switch (animationState) {
      case 'initial':
        return onAnimationStateInitial();
      case 'animating':
        return onAnimationStateAnimating();
      case 'finished':
        return onAnimationStateFinished();
    }
  }, [animationState]);

  return <HeroContext.Provider value={{ animationState, registerHeroCard }}>{children}</HeroContext.Provider>;
};

export const useHero = () => {
  const context = useContext(HeroContext);
  if (context === undefined) throw new Error('useHero must be used within a HeroProvider');
  return context;
};
